import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';

import { useUserContext } from 'src/components/app/context/UserContext';
import Modal from 'src/components/common/Modal';

import ContactModalContent from './components/ContactModalContent';
import DataProtectionModalContent from './components/DataProtectionModalContent';
import ImprintModalContent from './components/ImprintModalContent';

import { styles } from './index.styles';

export const MODAL = {
  CONTACT: 'contact',
  DATAPROTECTION: 'dataprotection',
  IMPRINT: 'imprint',
};

export const FOOTER_TEST_ID = 'footer';
export const FOOTER_CONTACT_TEST_ID = 'footer-contact';
export const FOOTER_IMPRINT_TEST_ID = 'footer-imprint';
export const FOOTER_DATA_PROTECTION_TEST_ID = 'footer-data-protection';

export interface FooterProps {}

const Footer: FC<FooterProps> = () => {
  const { user } = useUserContext();
  const { t } = useTranslation('common');
  const [displayedModal, setDisplayedModal] = useState('');

  const currentYear = new Date().getFullYear();

  const toggleModal = (modalToDisplay = '') =>
    setDisplayedModal(modalToDisplay);

  return (
    <Box component="footer" sx={styles.footer} data-test-id={FOOTER_TEST_ID}>
      <Stack
        direction="row"
        role="navigation"
        spacing={1.5}
        sx={{ justifyContent: 'center' }}
      >
        {user && (
          <Box data-test-id={FOOTER_CONTACT_TEST_ID}>
            <Button
              variant="text"
              sx={styles.footerButton}
              onClick={() => toggleModal(MODAL.CONTACT)}
            >
              {t('footer.Contact')}
            </Button>

            <Modal
              headline={t('footer.Contact')}
              isOpen={displayedModal === MODAL.CONTACT}
              onClose={() => toggleModal()}
            >
              <ContactModalContent />
            </Modal>
          </Box>
        )}

        <Box data-test-id={FOOTER_IMPRINT_TEST_ID}>
          <Button
            variant="text"
            sx={styles.footerButton}
            onClick={() => toggleModal(MODAL.IMPRINT)}
          >
            {t('footer.ImprintMasthead')}
          </Button>

          <Modal
            headline={t('footer.ImprintMasthead')}
            isOpen={displayedModal === MODAL.IMPRINT}
            onClose={() => toggleModal()}
          >
            <ImprintModalContent />
          </Modal>
        </Box>

        <Box data-test-id={FOOTER_DATA_PROTECTION_TEST_ID}>
          <Button
            variant="text"
            sx={styles.footerButton}
            onClick={() => toggleModal(MODAL.DATAPROTECTION)}
          >
            {t('footer.DataProtection')}
          </Button>

          <Modal
            headline={t('footer.DataProtection')}
            isOpen={displayedModal === MODAL.DATAPROTECTION}
            onClose={() => toggleModal()}
          >
            <DataProtectionModalContent />
          </Modal>
        </Box>
      </Stack>
      <Box sx={styles.footerClaim}>
        {t('footer.footerText', { currentYear })}
      </Box>
    </Box>
  );
};

export default Footer;
